import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DeleteActionButton from '../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../components/designe/ActionButtons/EditActionButton';
import { AppBarWithActionButtons } from '../../../components/designe/app-bars/app-bar-with-buttons/AppBarWithActionButtons';
import AppExerciseAvatar from '../../../components/designe/app-entity-avatar/app-exercise-avatar/AppExerciseAvatar';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import { successToastMessage } from '../../../components/layout/toast-root/toast';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import SetOptionTable from '../../../components/reusable/set-options-table/SetOptionTable';
import { SetOptionTableMode } from '../../../components/reusable/set-options-table/interfaces';
import ViewField from '../../../components/reusable/view-field/ViewField';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import withRouter from '../../../hoc/with-router';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { usePostPutProgramSet } from '../../../redux/api/programsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { AvatarSize } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { getEmptyProgramSetFromProgramSet } from '../../../utils/training/set-utils';
import { useStyles } from './ViewProgramSet.styles';
import { useViewProgramSetComponent } from './useViewProgramSetComponent';

const ViewProgramSet = (props) => {
  const { params } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const {
    programSet,
    isLoading,
    isError,
    error,
    refetch,
    handleEdit,
    handleDelete,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
  } = useViewProgramSetComponent(params.setId);
  const { navigate } = useNavigationStack();
  const [postPutProgramSet] = usePostPutProgramSet();
  const { data: user } = useGetMyDetails();
  const { showSaveSetButton } = useUserSetPreferences();
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);
  useRegisterPageContainerError({
    errors: isError && error ? [error] : [],
    isLoading: !programSet && isLoading,
    hasData: Boolean(programSet),
    onRetry: handleRetry,
  });

  function onExerciseClick(exerciseId: string) {
    navigate(Paths.exercises.toSummaryId(exerciseId));
  }

  const onSaveSet = useCallback(
    (set: IProgramSet, title: string) => {
      const newProgramSet: IProgramSet = getEmptyProgramSetFromProgramSet(set);

      newProgramSet.title = title;
      newProgramSet.ownerId = user?.id;
      newProgramSet.setNumber = 0;
      newProgramSet.orderNumber = 0;
      newProgramSet.exercise = null;

      return postPutProgramSet({
        setId: undefined,
        body: newProgramSet,
      })
        .unwrap()
        .then((response) => {
          successToastMessage(t('setTable.setSaved'));
          return response;
        })
        .catch((e) => {
          console.log(e);
          return null;
        });
    },
    [postPutProgramSet, user, t],
  );

  return (
    <PageContainer title={t('programs.programSet')} backButton>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        minWidth={0}>
        <Typography variant="h1" className={classes.programSetTitle}>
          {programSet?.title}
        </Typography>
        <Box display="flex" gap={0.5} flexShrink={0}>
          {programSet?.permissions?.canEdit && (
            <EditActionButton onClick={() => handleEdit()} />
          )}
          {programSet?.permissions?.canDelete && (
            <DeleteActionButton
              onClick={() => setShowDeleteConfirmation(true)}
            />
          )}
        </Box>
      </Box>

      {programSet?.description && (
        <ViewField
          value={programSet.description}
          label={t('common.description')}
        />
      )}
      <Box
        display={'flex'}
        flexDirection="column"
        gap={0.5}
        className={classes.setWrapper}>
        <Typography variant="label-text-sm">{t('common.exercise')}</Typography>

        <Box
          display="flex"
          alignItems="flex-start"
          gap={1}
          className={classes.linkWrapper}
          onClick={() => onExerciseClick(programSet?.exercise?.id)}>
          <AppExerciseAvatar
            exercise={programSet?.exercise}
            size={AvatarSize.s}
          />
          <Typography variant="text-lg" className={classes.exerciseTitleInRow}>
            {programSet?.exercise?.title}
          </Typography>
        </Box>
        {programSet && (
          <Box marginTop={1}>
            <SetOptionTable
              set={programSet}
              mode={SetOptionTableMode.program}
              showSetsColumn={true}
              showRestColumn={true}
              canSaveSet={showSaveSetButton}
              onSaveSet={(title) => onSaveSet(programSet, title)}
            />
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title={t('programs.deleteProgramSet')}
        onSubmit={() => handleDelete()}>
        {t('programs.deleteProgramSetQuestion')}
      </ConfirmDialog>
    </PageContainer>
  );
};

export default withRouter(ViewProgramSet);
